import { netScore } from './handicaps'

/**
 * Get a player's gross score on a specific hole.
 */
export function getScore(scores, playerId, hole) {
  const s = scores.find((sc) => sc.player_id === playerId && sc.hole_number === hole)
  return s?.strokes ?? null
}

/**
 * Get number of strokes a player receives on a hole from the handicap breakdown.
 */
export function getStrokesOnHole(handicapBreakdown, playerId, hole) {
  const hcp = handicapBreakdown?.find((h) => h.playerId === playerId)
  return hcp?.strokeHoles?.get(hole) || 0
}

/**
 * Get a player's gross total across all scored holes.
 */
export function getTotal(scores, playerId) {
  return scores
    .filter((sc) => sc.player_id === playerId && sc.strokes != null)
    .reduce((sum, sc) => sum + sc.strokes, 0)
}

/**
 * Get a player's net total across all scored holes.
 */
export function getNetTotal(scores, handicapBreakdown, playerId) {
  if (!handicapBreakdown) return null
  return scores
    .filter((sc) => sc.player_id === playerId && sc.strokes != null)
    .reduce((sum, sc) => {
      const strk = getStrokesOnHole(handicapBreakdown, playerId, sc.hole_number)
      return sum + netScore(sc.strokes, strk)
    }, 0)
}

/**
 * Get whether a player's score on a hole is taken or dropped (Six Pack game).
 */
export function getScoreTaken(scores, playerId, hole) {
  const s = scores.find((sc) => sc.player_id === playerId && sc.hole_number === hole)
  return s?.score_taken ?? null
}

/**
 * Format a match play hole differential for display.
 * Positive = Team A leading, negative = Team A trailing.
 */
export function formatMatchDiff(diff) {
  if (diff == null) return null
  if (diff === 0) return { text: 'AS', color: 'var(--text-muted)' }
  if (diff > 0) return { text: `${diff} UP`, color: 'var(--accent-primary)' }
  return { text: `${Math.abs(diff)} DN`, color: 'var(--accent-danger, #ef4444)' }
}

/**
 * Format a point differential for display (used by High-Low).
 * Positive = Team A leading, negative = Team A trailing.
 */
export function formatPointDiff(diff) {
  if (diff == null) return null
  if (diff === 0) return { text: 'T', color: 'var(--text-muted)' }
  if (diff > 0) return { text: `+${diff}`, color: 'var(--accent-primary)' }
  return { text: `${diff}`, color: 'var(--accent-danger, #ef4444)' }
}

/**
 * Convert computeSegmentMatchStatus result to Team A perspective text.
 * That function always returns "X UP" from winner's perspective; flip when Team B won.
 */
export function teamAResultText(segResult) {
  if (!segResult || segResult.winner == null) return null
  const num = parseInt(segResult.text)
  if (segResult.winner === 0) return `${num}UP`
  return `${num}DN`
}
